import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { 
  DollarSign, TrendingUp, Users, CreditCard, Gift, Lock,
  Crown, BarChart3, ArrowUpRight, ArrowDownRight, Plus,
  Eye, Heart, MessageCircle, Loader2, ChevronRight,
  Wallet, History, Settings, Star, CheckCircle2, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CreatorDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [paidContent, setPaidContent] = useState([]);
  const [payouts, setPayouts] = useState([]);
  
  // Create content modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newContent, setNewContent] = useState({
    title: '',
    description: '',
    content: '',
    price: 4.99,
    content_type: 'post',
    preview_content: '',
    is_subscriber_free: false
  });
  
  // Payout modal
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [requestingPayout, setRequestingPayout] = useState(false);
  
  // Scheduling state
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [scheduleForm, setScheduleForm] = useState({ content: '', scheduled_for: '' });
  const [scheduling, setScheduling] = useState(false);
  
  // Embed state
  const [embedPostId, setEmbedPostId] = useState('');
  const [embedTheme, setEmbedTheme] = useState('light');
  const [embedCode, setEmbedCode] = useState('');
  
  // Check access
  const allowedTiers = ['pro', 'enterprise', 'supremium', 'enterprise_supremium', 'nonprofit_enterprise', 'nonprofit_supremium'];
  const hasAccess = allowedTiers.includes(user?.subscription_tier) || user?.is_admin;
  
  useEffect(() => {
    if (hasAccess) {
      fetchData();
      loadScheduledPosts();
    }
  }, [hasAccess]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [earningsRes, analyticsRes, subscribersRes, contentRes, payoutsRes] = await Promise.all([
        axios.get(`${API_URL}/monetization/earnings`, { withCredentials: true }),
        axios.get(`${API_URL}/monetization/analytics?period=30d`, { withCredentials: true }),
        axios.get(`${API_URL}/monetization/subscribers?limit=10`, { withCredentials: true }),
        axios.get(`${API_URL}/monetization/content/my?limit=10`, { withCredentials: true }),
        axios.get(`${API_URL}/monetization/payouts?limit=5`, { withCredentials: true })
      ]);
      
      setEarnings(earningsRes.data);
      setAnalytics(analyticsRes.data);
      setSubscribers(subscribersRes.data.subscribers || []);
      setPaidContent(contentRes.data.content || []);
      setPayouts(payoutsRes.data.payouts || []);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadScheduledPosts = async () => {
    try {
      const res = await axios.get(`${API_URL}/scheduling/`, { withCredentials: true });
      setScheduledPosts(res.data.scheduled_posts || res.data || []);
    } catch (e) { /* silent */ }
  };

  const handleSchedulePost = async () => {
    if (!scheduleForm.content || !scheduleForm.scheduled_for) {
      toast.error('Content and scheduled time are required');
      return;
    }
    setScheduling(true);
    try {
      await axios.post(`${API_URL}/scheduling/schedule`, {
        content: scheduleForm.content,
        scheduled_for: new Date(scheduleForm.scheduled_for).toISOString()
      }, { withCredentials: true });
      toast.success('Post scheduled!');
      setScheduleForm({ content: '', scheduled_for: '' });
      loadScheduledPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule');
    } finally {
      setScheduling(false);
    }
  };

  const cancelScheduled = async (id) => {
    try {
      await axios.delete(`${API_URL}/scheduling/${id}`, { withCredentials: true });
      toast.success('Scheduled post cancelled');
      loadScheduledPosts();
    } catch (e) {
      toast.error('Failed to cancel');
    }
  };

  const generateEmbedCode = () => {
    if (!embedPostId.trim()) {
      toast.error('Enter a post ID');
      return;
    }
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const code = `<iframe src="${baseUrl}/api/embed/post/${embedPostId.trim()}?theme=${embedTheme}" width="500" height="400" frameborder="0" style="border-radius:12px;overflow:hidden;" allowfullscreen></iframe>`;
    setEmbedCode(code);
    toast.success('Embed code generated!');
  };

  const createPaidContent = async () => {
    if (!newContent.title || !newContent.content || !newContent.price) {
      toast.error('Please fill in title, content, and price');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API_URL}/monetization/content/create`, newContent, { withCredentials: true });
      toast.success('Paid content created!');
      setShowCreateModal(false);
      setNewContent({
        title: '',
        description: '',
        content: '',
        price: 4.99,
        content_type: 'post',
        preview_content: '',
        is_subscriber_free: false
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create content');
    } finally {
      setCreating(false);
    }
  };
  
  const requestPayout = async () => {
    const amount = payoutAmount ? parseFloat(payoutAmount) : null;
    
    setRequestingPayout(true);
    try {
      const response = await axios.post(`${API_URL}/monetization/payout/request`, {
        amount,
        payout_method: 'stripe'
      }, { withCredentials: true });
      
      toast.success(`Payout of $${response.data.amount} requested!`);
      setShowPayoutModal(false);
      setPayoutAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };
  
  // Non-access users see upgrade prompt
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mx-auto mb-6">
              <DollarSign className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl font-heading font-black text-gray-900 dark:text-white mb-4">
              Creator Monetization
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-2">
              Turn your content into income
            </p>
            <p className="text-lg text-amber-600 font-semibold mb-8">
              Available for <span className="text-orange-600">Pro</span> and higher tiers
            </p>
            
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-amber-200 dark:border-amber-800 mb-8">
              <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">Monetization Features:</h3>
              <ul className="space-y-3 text-left max-w-md mx-auto">
                {[
                  'Receive tips from fans ($1-$500)',
                  'Create paid/exclusive content',
                  'Offer subscription tiers (Basic, Premium, VIP)',
                  'Detailed earnings analytics',
                  'Easy payouts to your bank account'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-amber-500" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            
            <Button
              onClick={() => navigate('/pricing')}
              className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold px-8 h-14 text-lg hover:scale-105 transition-transform"
              data-testid="upgrade-for-monetization-btn"
            >
              <Crown className="w-5 h-5 mr-2" />
              Upgrade to Pro - $9.99/month
            </Button>
          </motion.div>
        </div>
        <FloatingDock user={user} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-amber-200 dark:border-amber-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-gray-900 dark:text-white">
                  Creator Dashboard
                </h1>
                <p className="text-sm text-gray-500">Manage your earnings & content</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowCreateModal(true)}
                className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                data-testid="create-paid-content-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Paid Content
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <Wallet className="w-4 h-4" />
                  <span className="text-sm">Available</span>
                </div>
                <div className="text-2xl font-bold text-green-600">
                  ${earnings?.available_balance?.toFixed(2) || '0.00'}
                </div>
                <Button
                  variant="link"
                  className="text-xs text-amber-600 p-0 h-auto"
                  onClick={() => setShowPayoutModal(true)}
                  disabled={!earnings?.available_balance || earnings?.available_balance < 25}
                >
                  Request Payout →
                </Button>
              </Card>
              
              <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm">Total Earned</span>
                </div>
                <div className="text-2xl font-bold text-amber-600">
                  ${earnings?.total_earnings?.toFixed(2) || '0.00'}
                </div>
              </Card>
              
              <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Subscribers</span>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {earnings?.subscriber_count || 0}
                </div>
              </Card>
              
              <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <Gift className="w-4 h-4" />
                  <span className="text-sm">Tips</span>
                </div>
                <div className="text-2xl font-bold text-pink-600">
                  {earnings?.breakdown?.tips?.count || 0}
                </div>
              </Card>
            </div>

            {/* Earnings Breakdown */}
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <Card className="p-4 bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 rounded-2xl border-pink-200 dark:border-pink-800">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-5 h-5 text-pink-500" />
                  <span className="font-semibold text-gray-900 dark:text-white">Tips</span>
                  <Badge className="ml-auto bg-pink-100 text-pink-700">10% fee</Badge>
                </div>
                <div className="text-3xl font-bold text-pink-600">
                  ${earnings?.breakdown?.tips?.total?.toFixed(2) || '0.00'}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {earnings?.breakdown?.tips?.count || 0} tips received
                </p>
              </Card>
              
              <Card className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-5 h-5 text-amber-500" />
                  <span className="font-semibold text-gray-900 dark:text-white">Content Sales</span>
                  <Badge className="ml-auto bg-amber-100 text-amber-700">15% fee</Badge>
                </div>
                <div className="text-3xl font-bold text-amber-600">
                  ${earnings?.breakdown?.content_sales?.total?.toFixed(2) || '0.00'}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {earnings?.breakdown?.content_sales?.count || 0} purchases
                </p>
              </Card>
              
              <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-blue-500" />
                  <span className="font-semibold text-gray-900 dark:text-white">Subscriptions</span>
                  <Badge className="ml-auto bg-blue-100 text-blue-700">20% fee</Badge>
                </div>
                <div className="text-3xl font-bold text-blue-600">
                  ${earnings?.breakdown?.subscriptions?.monthly_estimate?.toFixed(2) || '0.00'}/mo
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {earnings?.breakdown?.subscriptions?.active_count || 0} active subscribers
                </p>
              </Card>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="grid w-full grid-cols-4 bg-amber-100 dark:bg-gray-800 rounded-2xl p-1">
                <TabsTrigger value="overview" className="rounded-xl data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="content" className="rounded-xl data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
                  <Lock className="w-4 h-4 mr-2" />
                  Content
                </TabsTrigger>
                <TabsTrigger value="subscribers" className="rounded-xl data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
                  <Users className="w-4 h-4 mr-2" />
                  Subscribers
                </TabsTrigger>
                <TabsTrigger value="payouts" className="rounded-xl data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Payouts
                </TabsTrigger>
                <TabsTrigger value="scheduling" className="rounded-xl data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
                  <History className="w-4 h-4 mr-2" />
                  Schedule
                </TabsTrigger>
                <TabsTrigger value="embeds" className="rounded-xl data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
                  <Settings className="w-4 h-4 mr-2" />
                  Embeds
                </TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Recent Tips */}
                  <Card className="p-6 bg-white dark:bg-gray-800 rounded-3xl">
                    <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                      <Gift className="w-5 h-5 text-pink-500" />
                      Recent Tips
                    </h3>
                    {analytics?.recent_tips?.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.recent_tips.slice(0, 5).map((tip, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                            <div>
                              <p className="font-semibold text-gray-900 dark:text-white">
                                ${tip.amount?.toFixed(2)}
                              </p>
                              {tip.message && (
                                <p className="text-sm text-gray-500 truncate max-w-[200px]">
                                  "{tip.message}"
                                </p>
                              )}
                            </div>
                            <Badge className="bg-green-100 text-green-700">
                              +${tip.creator_amount?.toFixed(2)}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No tips yet</p>
                    )}
                  </Card>

                  {/* Top Content */}
                  <Card className="p-6 bg-white dark:bg-gray-800 rounded-3xl">
                    <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-amber-500" />
                      Top Performing Content
                    </h3>
                    {analytics?.top_content?.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.top_content.map((content, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                            <div>
                              <p className="font-semibold text-gray-900 dark:text-white truncate max-w-[200px]">
                                {content.title}
                              </p>
                              <p className="text-sm text-gray-500">
                                {content.purchase_count} sales
                              </p>
                            </div>
                            <Badge className="bg-amber-100 text-amber-700">
                              ${content.total_revenue?.toFixed(2)}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-500 mb-4">No paid content yet</p>
                        <Button
                          onClick={() => setShowCreateModal(true)}
                          variant="outline"
                          className="rounded-full"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Create Your First
                        </Button>
                      </div>
                    )}
                  </Card>
                </div>
              </TabsContent>

              {/* Content Tab */}
              <TabsContent value="content" className="space-y-6">
                <Card className="p-6 bg-white dark:bg-gray-800 rounded-3xl">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-bold text-lg text-gray-900 dark:text-white">Your Paid Content</h3>
                    <Button
                      onClick={() => setShowCreateModal(true)}
                      className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create New
                    </Button>
                  </div>
                  
                  {paidContent.length > 0 ? (
                    <div className="space-y-4">
                      {paidContent.map((content) => (
                        <div 
                          key={content.content_id}
                          className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-amber-300 transition-colors"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-bold text-gray-900 dark:text-white">{content.title}</h4>
                              <Badge className={content.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                                {content.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              {content.is_subscriber_free && (
                                <Badge className="bg-blue-100 text-blue-700">Free for subs</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{content.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-3 h-3" />
                                ${content.price}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {content.purchase_count} sales
                              </span>
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                {content.views_count} views
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-green-600">
                              ${content.total_revenue?.toFixed(2) || '0.00'}
                            </p>
                            <p className="text-xs text-gray-500">earned</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Lock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h4 className="font-bold text-xl text-gray-900 dark:text-white mb-2">No Paid Content Yet</h4>
                      <p className="text-gray-500 mb-4">Create exclusive content for your fans</p>
                    </div>
                  )}
                </Card>
              </TabsContent>

              {/* Subscribers Tab */}
              <TabsContent value="subscribers" className="space-y-6">
                <Card className="p-6 bg-white dark:bg-gray-800 rounded-3xl">
                  <h3 className="font-bold text-lg mb-6 text-gray-900 dark:text-white">Your Subscribers</h3>
                  
                  {subscribers.length > 0 ? (
                    <div className="space-y-3">
                      {subscribers.map((sub) => (
                        <div 
                          key={sub.subscription_id}
                          className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">
                              {sub.subscriber?.name?.[0] || sub.subscriber?.username?.[0] || '?'}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 dark:text-white">
                                {sub.subscriber?.name || sub.subscriber?.username}
                              </p>
                              <p className="text-sm text-gray-500">
                                @{sub.subscriber?.username}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className={`${
                              sub.tier === 'vip' ? 'bg-purple-100 text-purple-700' :
                              sub.tier === 'premium' ? 'bg-amber-100 text-amber-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {sub.tier?.toUpperCase()}
                            </Badge>
                            <p className="text-sm text-gray-500 mt-1">
                              ${sub.price}/mo
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h4 className="font-bold text-xl text-gray-900 dark:text-white mb-2">No Subscribers Yet</h4>
                      <p className="text-gray-500">Share your profile to get subscribers</p>
                    </div>
                  )}
                </Card>
              </TabsContent>

              {/* Payouts Tab */}
              <TabsContent value="payouts" className="space-y-6">
                <Card className="p-6 bg-white dark:bg-gray-800 rounded-3xl">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-bold text-lg text-gray-900 dark:text-white">Payout History</h3>
                    <Button
                      onClick={() => setShowPayoutModal(true)}
                      disabled={!earnings?.available_balance || earnings?.available_balance < 25}
                      className="rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Request Payout
                    </Button>
                  </div>
                  
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 mb-6">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      <strong>Minimum payout:</strong> $25 | 
                      <strong> Processing time:</strong> 3-5 business days
                    </p>
                  </div>
                  
                  {payouts.length > 0 ? (
                    <div className="space-y-3">
                      {payouts.map((payout) => (
                        <div 
                          key={payout.payout_id}
                          className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700"
                        >
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              ${payout.amount?.toFixed(2)}
                            </p>
                            <p className="text-sm text-gray-500">
                              {new Date(payout.requested_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge className={`${
                            payout.status === 'completed' ? 'bg-green-100 text-green-700' :
                            payout.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {payout.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h4 className="font-bold text-xl text-gray-900 dark:text-white mb-2">No Payouts Yet</h4>
                      <p className="text-gray-500">Your payout history will appear here</p>
                    </div>
                  )}
                </Card>
              </TabsContent>

              {/* Scheduling Tab */}
              <TabsContent value="scheduling" className="space-y-6">
                <Card className="p-6 rounded-2xl">
                  <h3 className="font-bold text-lg mb-4">Schedule a Post</h3>
                  <div className="space-y-3">
                    <Textarea
                      value={scheduleForm.content}
                      onChange={(e) => setScheduleForm(p => ({ ...p, content: e.target.value }))}
                      placeholder="Write your post content..."
                      rows={3}
                      className="rounded-xl"
                      data-testid="schedule-content-input"
                    />
                    <div className="flex items-center gap-3">
                      <Input
                        type="datetime-local"
                        value={scheduleForm.scheduled_for}
                        onChange={(e) => setScheduleForm(p => ({ ...p, scheduled_for: e.target.value }))}
                        className="rounded-xl flex-1"
                        data-testid="schedule-datetime-input"
                      />
                      <Button
                        onClick={handleSchedulePost}
                        disabled={scheduling}
                        className="rounded-full bg-primary text-white"
                        data-testid="schedule-post-btn"
                      >
                        {scheduling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Schedule'}
                      </Button>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 rounded-2xl">
                  <h3 className="font-bold text-lg mb-4">Scheduled Posts ({scheduledPosts.length})</h3>
                  {scheduledPosts.length === 0 ? (
                    <p className="text-gray-500 text-sm">No scheduled posts</p>
                  ) : (
                    <div className="space-y-3">
                      {scheduledPosts.map((sp) => (
                        <div key={sp.schedule_id || sp.scheduled_id} className="flex items-start justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate">{sp.content}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Scheduled: {new Date(sp.scheduled_for || sp.scheduled_at).toLocaleString()}
                            </p>
                            <span className={`text-xs px-2 py-0.5 rounded-full mt-1 inline-block ${sp.status === 'scheduled' ? 'bg-yellow-100 text-yellow-700' : sp.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {sp.status}
                            </span>
                          </div>
                          {sp.status === 'scheduled' && (
                            <Button variant="ghost" size="sm" onClick={() => cancelScheduled(sp.schedule_id || sp.scheduled_id)} className="text-red-500 hover:text-red-700">
                              Cancel
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>

              {/* Embeds Tab */}
              <TabsContent value="embeds" className="space-y-6">
                <Card className="p-6 rounded-2xl">
                  <h3 className="font-bold text-lg mb-4">Embeddable Widgets</h3>
                  <p className="text-sm text-gray-500 mb-4">Generate embed codes to share your posts on other websites.</p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Input
                        value={embedPostId}
                        onChange={(e) => setEmbedPostId(e.target.value)}
                        placeholder="Enter post ID (e.g., post_37b8edf31109)"
                        className="rounded-xl flex-1"
                        data-testid="embed-post-id-input"
                      />
                      <select
                        value={embedTheme}
                        onChange={(e) => setEmbedTheme(e.target.value)}
                        className="rounded-xl border p-2 text-sm"
                      >
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>
                      </select>
                      <Button onClick={generateEmbedCode} className="rounded-full bg-primary text-white" data-testid="generate-embed-btn">
                        Generate
                      </Button>
                    </div>
                    {embedCode && (
                      <div className="space-y-2">
                        <Textarea
                          value={embedCode}
                          readOnly
                          rows={3}
                          className="rounded-xl text-xs font-mono bg-gray-50 dark:bg-gray-800"
                          data-testid="embed-code-output"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { navigator.clipboard.writeText(embedCode); toast.success('Copied!'); }}
                          className="rounded-full"
                        >
                          Copy to Clipboard
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>

                <Card className="p-6 rounded-2xl">
                  <h3 className="font-bold text-lg mb-2">Profile Widget</h3>
                  <p className="text-sm text-gray-500 mb-3">Embed your Socyy profile on any website.</p>
                  <Textarea
                    value={`<iframe src="${process.env.REACT_APP_BACKEND_URL}/api/embed/profile/${user?.username || user?.user_id}?theme=${embedTheme}" width="350" height="450" frameborder="0" style="border-radius:12px;overflow:hidden;" allowfullscreen></iframe>`}
                    readOnly
                    rows={3}
                    className="rounded-xl text-xs font-mono bg-gray-50 dark:bg-gray-800"
                    data-testid="profile-embed-code"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`<iframe src="${process.env.REACT_APP_BACKEND_URL}/api/embed/profile/${user?.username || user?.user_id}?theme=${embedTheme}" width="350" height="450" frameborder="0" style="border-radius:12px;overflow:hidden;" allowfullscreen></iframe>`);
                      toast.success('Profile embed code copied!');
                    }}
                    className="rounded-full mt-2"
                  >
                    Copy Profile Embed
                  </Button>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      {/* Create Content Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-lg p-6 shadow-2xl max-h-[80vh] overflow-y-auto"
              data-testid="create-content-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Create Paid Content</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Title *</label>
                  <Input
                    value={newContent.title}
                    onChange={(e) => setNewContent({...newContent, title: e.target.value})}
                    placeholder="Exclusive behind-the-scenes..."
                    className="rounded-xl"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-2">Description</label>
                  <Input
                    value={newContent.description}
                    onChange={(e) => setNewContent({...newContent, description: e.target.value})}
                    placeholder="What will supporters get?"
                    className="rounded-xl"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-2">Preview (shown to non-buyers)</label>
                  <Textarea
                    value={newContent.preview_content}
                    onChange={(e) => setNewContent({...newContent, preview_content: e.target.value})}
                    placeholder="Teaser text..."
                    className="rounded-xl resize-none"
                    rows={2}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-2">Full Content *</label>
                  <Textarea
                    value={newContent.content}
                    onChange={(e) => setNewContent({...newContent, content: e.target.value})}
                    placeholder="The exclusive content your supporters will see..."
                    className="rounded-xl resize-none"
                    rows={4}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Price ($) *</label>
                    <Input
                      type="number"
                      min="0.99"
                      max="999"
                      step="0.01"
                      value={newContent.price}
                      onChange={(e) => setNewContent({...newContent, price: parseFloat(e.target.value)})}
                      className="rounded-xl"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold mb-2">Content Type</label>
                    <select
                      value={newContent.content_type}
                      onChange={(e) => setNewContent({...newContent, content_type: e.target.value})}
                      className="w-full p-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                    >
                      <option value="post">Post</option>
                      <option value="article">Article</option>
                      <option value="video">Video</option>
                      <option value="gallery">Gallery</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">Free for Subscribers</p>
                    <p className="text-sm text-gray-500">Your subscribers get this for free</p>
                  </div>
                  <Switch
                    checked={newContent.is_subscriber_free}
                    onCheckedChange={(val) => setNewContent({...newContent, is_subscriber_free: val})}
                  />
                </div>
                
                <Button
                  onClick={createPaidContent}
                  disabled={creating}
                  className="w-full h-12 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Create Paid Content (${newContent.price})
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payout Modal */}
      <AnimatePresence>
        {showPayoutModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setShowPayoutModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-md p-6 shadow-2xl"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Request Payout</h2>
              
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-600 dark:text-gray-300">Available Balance</p>
                <p className="text-3xl font-bold text-green-600">
                  ${earnings?.available_balance?.toFixed(2) || '0.00'}
                </p>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">Amount (leave empty for full balance)</label>
                <Input
                  type="number"
                  min="25"
                  max={earnings?.available_balance || 0}
                  step="0.01"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  placeholder={`Up to $${earnings?.available_balance?.toFixed(2) || '0.00'}`}
                  className="rounded-xl"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowPayoutModal(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={requestPayout}
                  disabled={requestingPayout}
                  className="flex-1 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                >
                  {requestingPayout ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Request Payout'
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <FloatingDock user={user} />
    </div>
  );
};

export default CreatorDashboard;
